import type { ApplyDesignModelResponse, Dashboard, DesignDocument, DesignModel, DesignModelSlug, DesignModelSummary, EstimatingDemand } from '@forgd/contract/types'
import { withTrailingSlash } from 'ufo'

export function useDesignTokenomicsApi() {
  const runtimeConfig = useRuntimeConfig()
  const supabase = useSupabaseClient()
  const baseURL = withTrailingSlash(runtimeConfig.public.apiUrl)

  async function authHeaders() {
    const { data: { session } } = await supabase.auth.getSession()

    return session ? { Authorization: `Bearer ${session.access_token}` } : undefined
  }

  return {
    fetchModels(): Promise<DesignModelSummary[]> {
      return $fetch<DesignModelSummary[]>('design-tokenomics/models', { baseURL })
    },

    fetchInitialDocument(): Promise<DesignDocument> {
      return $fetch<DesignDocument>('design-tokenomics/initial-document', { baseURL })
    },

    async fetchDocument(projectId: string): Promise<DesignDocument> {
      return $fetch<DesignDocument>(`design-tokenomics/${projectId}/document`, {
        baseURL,
        headers: await authHeaders(),
        credentials: 'include',
      })
    },

    fetchModel(slug: DesignModelSlug): Promise<DesignModel> {
      return $fetch<DesignModel>(`design-tokenomics/models/${slug}`, { baseURL })
    },

    fetchDemandPreview(model: DesignModel): Promise<EstimatingDemand> {
      return $fetch<EstimatingDemand>('design-tokenomics/demand-preview', {
        baseURL,
        method: 'POST',
        body: model,
      })
    },

    fetchDashboard(model: DesignModel): Promise<Dashboard> {
      return $fetch<Dashboard>('design-tokenomics/dashboard', {
        baseURL,
        method: 'POST',
        body: model,
      })
    },

    async setPublished(projectId: string, published: boolean): Promise<{ published: boolean }> {
      return $fetch<{ published: boolean }>(`design-tokenomics/${projectId}/published`, {
        baseURL,
        method: 'PUT',
        body: { published },
        headers: await authHeaders(),
        credentials: 'include',
      })
    },

    async applyDocument(projectId: string, document: DesignDocument): Promise<ApplyDesignModelResponse> {
      return $fetch<ApplyDesignModelResponse>(`design-tokenomics/${projectId}/design`, {
        baseURL,
        method: 'PUT',
        body: document,
        headers: await authHeaders(),
        credentials: 'include',
      })
    },
  }
}
